import { Ellipsis } from 'lucide-react';
import { ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';
import { Field } from '@/Domain/components/Shared/Field/Field';
import { FormControl } from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { HttpActionForm } from '@/Domain/hooks/Action/ApiAction/useHttpRequestActionFormV2.hook';

type Props = {
  field: ControllerRenderProps<HttpActionForm, `parameters.${number}.default_value`>;
  index: number;
  isBoolean: boolean;
  isAutomatic: boolean;
};

const NO_DEFAULT = 'unset';
const EMPTY_DEFAULT = 'empty';

export const ParameterDefaultValue = ({ field, index, isBoolean, isAutomatic }: Props) => {
  const { t } = useTranslation('actions_page', { keyPrefix: 'sheet.form.data_to_collect.table' });
  const noDefault = field.value === null;

  if (isBoolean) {
    const selection =
      noDefault || (isAutomatic && field.value === '')
        ? NO_DEFAULT
        : field.value === ''
          ? EMPTY_DEFAULT
          : String(Number(field.value));

    return (
      <FormControl>
        <Select
          value={selection}
          onValueChange={(value) =>
            field.onChange(
              value === NO_DEFAULT
                ? isAutomatic
                  ? ''
                  : null
                : value === EMPTY_DEFAULT
                  ? ''
                  : value,
            )
          }
        >
          <SelectTrigger
            data-testid="boolean-default-value"
            aria-label={t('default_value')}
            className="w-full"
            size="sm"
            ref={field.ref}
            onBlur={field.onBlur}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={NO_DEFAULT} data-testid={`parameter-default-none-${index}`}>
                {t(isAutomatic ? 'default_value_unset' : 'default_value_none')}
              </SelectItem>
              {!isAutomatic && field.value === '' && (
                <SelectItem value={EMPTY_DEFAULT}>{t('default_value_empty')}</SelectItem>
              )}
              <SelectItem value="1">True</SelectItem>
              <SelectItem value="0">False</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </FormControl>
    );
  }

  return (
    <Field orientation="horizontal" className="items-center">
      <FormControl>
        <Input
          {...field}
          value={field.value === null ? '' : String(field.value)}
          onChange={(event) =>
            field.onChange(!isAutomatic && event.target.value === '' ? null : event.target.value)
          }
          data-testid={`parameter-default-value-${index}`}
          aria-label={t('default_value')}
          size="sm"
          className="flex-1"
          placeholder={t(
            noDefault
              ? 'default_value_none'
              : !isAutomatic && field.value === ''
                ? 'default_value_empty'
                : 'default_value_placeholder',
          )}
        />
      </FormControl>
      {!isAutomatic && (
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <IconButton
                  variant="ghost"
                  size="xs"
                  className="shrink-0"
                  data-testid={`parameter-default-options-${index}`}
                  aria-label={t('default_value_options')}
                >
                  <Ellipsis />
                </IconButton>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>{t('default_value_options')}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end">
            <DropdownMenuGroup>
              <DropdownMenuItem
                data-testid={`parameter-default-none-${index}`}
                isSelected={noDefault}
                onSelect={() => field.onChange(null)}
              >
                {t('default_value_none')}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid={`parameter-default-empty-${index}`}
                isSelected={field.value === ''}
                onSelect={() => field.onChange('')}
              >
                {t('default_value_empty')}
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </Field>
  );
};
